import { AxiosError } from 'axios'
import chunk from 'lodash/chunk'
import { CustomerStore } from 'iam/CustomerStore'
import { ParsedCustomerRow } from 'utils/csv-parser'

/**
 * Result of a bulk customer submission.
 */
export interface BulkSubmissionResult {
  email: string
  givenName: string
  familyName: string
  success: boolean
  errorMessage?: string
}

const BATCH_SIZE = 25
const BATCH_DELAY_MS = 11000

/**
 * Creates customers in batches with a delay between batches to avoid rate limiting.
 *
 * @param customers - The list of customers to create
 * @param customerStore - The customer store to use for creation
 * @param workspaceId - The workspace ID to create customers in
 * @returns Array of results for each customer
 */
export async function bulkCreateCustomers(
  customers: ParsedCustomerRow[],
  customerStore: CustomerStore,
  workspaceId: string,
  advisorUserPublicId?: string,
): Promise<BulkSubmissionResult[]> {
  const batches = chunk(customers, BATCH_SIZE)
  const results: BulkSubmissionResult[] = []

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i]

    const batchResults = await Promise.all(
      batch.map(async (customer) => {
        try {
          await customerStore.create({
            workspace_id: workspaceId,
            email: customer.email,
            given_name: customer.given_name,
            family_name: customer.family_name,
            phone_number: customer.phone_number || undefined,
            advisor_user_public_id: advisorUserPublicId,
          })
          return {
            email: customer.email,
            givenName: customer.given_name,
            familyName: customer.family_name,
            success: true,
          }
        } catch (err) {
          const axiosError = err as AxiosError<{ message?: string }>
          const errorMessage =
            axiosError.response?.data?.message ||
            axiosError.message ||
            'Unknown error'
          return {
            email: customer.email,
            givenName: customer.given_name,
            familyName: customer.family_name,
            success: false,
            errorMessage,
          }
        }
      }),
    )

    results.push(...batchResults)

    // Add delay between batches (skip delay after the last batch)
    if (i < batches.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS))
    }
  }

  return results
}
